import { getContactsAsync, requestPermissionsAsync } from 'expo-contacts'
import { RefObject, useCallback, useEffect, useState } from 'react'
import { FlatList, Keyboard, TouchableOpacity, View, Text, StyleSheet } from 'react-native'
import SearchBar from '@/src/common/components/SearchBar'
import { sendSMSAsync } from 'expo-sms'
import { useReferralShareMessage } from './_useReferralShareMessage'
import { useCardReferralCode } from '@/src/common/hooks/useCardReferralCode'
import { SlideInModal } from '@/src/common/components/SlideInModal'
import BottomSheet from '@gorhom/bottom-sheet'
import { useSafeAreaFrame } from 'react-native-safe-area-context'
import { mesaColorNames, typography } from '@/src/common/theme'
import { Separator } from 'tamagui'

interface Contact {
  name: string
  phoneNumber: string
}

/**
 * Keys are in the format `name/phoneNumber`
 */
type ContactRecord = Record<string, Contact>

function getContactKey(contact: Contact) {
  return `${contact.name}/${contact.phoneNumber}` as const
}

export function ContactPicker({
  bottomSheetRef,
}: {
  bottomSheetRef: RefObject<BottomSheet | null>
}) {
  const referralCode = useCardReferralCode()
  const { shareMessageWithCode, shareSheetLink } = useReferralShareMessage(referralCode)
  const { height: safeAreaHeight } = useSafeAreaFrame()

  const [contacts, setContacts] = useState<ContactRecord>({})
  const [filteredContacts, setFilteredContacts] = useState<ContactRecord>({})
  const [isOpen, setIsOpen] = useState(false)

  const messageWithCodeAndLink = `${shareMessageWithCode}\n\n${shareSheetLink}`

  useEffect(() => {
    async function fetchContacts() {
      if (!isOpen) {
        return
      }
      const response = await requestPermissionsAsync()
      if (!response.granted) {
        return
      }

      const { data } = await getContactsAsync()
      const contactData = data.reduce<ContactRecord>((acc, contact) => {
        contact.phoneNumbers?.forEach((phoneNumber) => {
          const newContact = {
            name:
              contact.name ||
              `${contact.firstName ? contact.firstName + ' ' : ''}${contact.lastName || ''}` ||
              '',
            phoneNumber: phoneNumber.number ?? '',
          }
          acc[getContactKey(newContact)] = newContact
        })
        return acc
      }, {})
      setContacts(contactData)
      setFilteredContacts(contactData)
    }
    void fetchContacts()
  }, [isOpen])

  const renderContactRow = useCallback(
    ({ item, index }: { item: Contact; index: number }) => (
      <ContactRow item={item} index={index} message={messageWithCodeAndLink} />
    ),
    [messageWithCodeAndLink],
  )

  return (
    <SlideInModal
      ref={bottomSheetRef}
      title="Connect contacts"
      maxDynamicContentSize={safeAreaHeight - 64}
      onChange={(index) => {
        setIsOpen(index !== -1)
      }}
      onClose={() => {
        Keyboard.dismiss()
      }}
    >
      <View style={styles.container}>
        <Text style={styles.text}>
          Easily refer your friends to Mesa! Connect your contacts and select one or multiple
          friends to send a referral text. Share the benefits of Mesa in just a few clicks!
        </Text>
        <SearchBar
          onSearch={(searchQuery: string) => {
            setFilteredContacts(
              Object.entries(contacts).reduce<ContactRecord>((acc, [key, contact]) => {
                if (
                  contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  contact.phoneNumber.toLowerCase().includes(searchQuery.toLowerCase())
                ) {
                  acc[key] = contact
                }
                return acc
              }, {}),
            )
          }}
          placeholder="Search contacts by name, number"
          debounceOptions={{
            mountArgs: [contacts],
          }}
        />
        <FlatList
          style={styles.contactList}
          data={Object.values(filteredContacts)}
          renderItem={renderContactRow}
          keyExtractor={(item, index) => `${getContactKey(item)}/${index}`}
        />
      </View>
    </SlideInModal>
  )
}

function ContactRow({ item, index, message }: { item: Contact; index: number; message: string }) {
  const { name, phoneNumber } = item

  const [hasSentInvite, setHasSentInvite] = useState(false)

  const onPressSendInvites = useCallback(async () => {
    const { result } = await sendSMSAsync([phoneNumber], message)
    if (result === 'sent') {
      setHasSentInvite(true)
    }
  }, [phoneNumber, message])

  return (
    <View>
      {index ? (
        <Separator
          style={[styles.divider, { borderColor: mesaColorNames['global/grey/20 #E6E9EB'] }]}
        />
      ) : null}
      <View style={styles.contactRow}>
        <View style={styles.contactInfoContainer}>
          <Text style={styles.contactName}>{name}</Text>
          <Text style={styles.contactPhoneNumber}>{phoneNumber}</Text>
        </View>
        <TouchableOpacity
          disabled={hasSentInvite}
          onPress={() => void onPressSendInvites()}
          style={[styles.inviteButton, hasSentInvite && styles.inviteButtonDisabled]}
        >
          <Text style={[styles.inviteButtonText, hasSentInvite && styles.inviteButtonTextDisabled]}>
            {hasSentInvite ? 'Sent' : 'Invite'}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    gap: 32,
  },
  text: {
    ...typography['paragraph/md'],
    textAlign: 'center',
    marginHorizontal: 21.5,
  },
  contactList: {
    paddingHorizontal: 16,
    minHeight: 200,
  },
  divider: {
    marginTop: 12,
    marginBottom: 12,
    height: 1,
  },
  contactRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  contactInfoContainer: {
    gap: 8,
  },
  contactName: {
    ...typography['paragraph/md'],
    color: '#13124',
  },
  contactPhoneNumber: {
    ...typography['paragraph/md'],
    color: mesaColorNames['Greyscale/500 #6E7375'],
  },
  inviteButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 100,
    backgroundColor: mesaColorNames['Greyscale/200 #EBF0F1'],
  },
  inviteButtonDisabled: {
    backgroundColor: '#F5F8F8',
  },
  inviteButtonTextDisabled: {
    color: mesaColorNames['semantic/fg/disabled #898D8F'],
  },
  inviteButtonText: {
    ...typography['paragraph/md'],
    color: '#13124',
  },
})
